import { useMemo } from 'react';

import { Copy } from 'lucide-react';

import { Button } from '@/_components';
import { getInvoiceRecipientEmailAddresses } from '@/_pages/invoices/shared/get-invoice-email-recipients';
import type { InvoiceQuerySchema } from '@/_pages/invoices/shared/schemas';
import { snackbar, useCopyToClipboard } from '@/_shared';

type InvoiceCopyRecipientEmailsButtonProps = {
  invoice: InvoiceQuerySchema;
};

export function InvoiceCopyRecipientEmailsButton({ invoice }: InvoiceCopyRecipientEmailsButtonProps) {
  const { copy } = useCopyToClipboard();
  const emails = useMemo(() => getInvoiceRecipientEmailAddresses(invoice), [invoice]);
  const hasEmails = emails.length > 0;

  const handleCopy = async () => {
    if (!hasEmails) {
      snackbar.error('No recipient emails to copy');
      return;
    }

    const ok = await copy(emails.join('; '));
    if (!ok) {
      snackbar.error('Could not copy to clipboard');
      return;
    }

    const label = emails.length === 1 ? 'recipient email' : 'recipient emails';
    snackbar.success(`Copied ${emails.length} ${label} to clipboard`);
  };

  return (
    <Button
      type="button"
      as="link"
      tag="button"
      size="xs"
      icon={Copy}
      aria-label="Copy recipient emails"
      iconClassName="size-3.5 shrink-0"
      className="text-c-card-muted hover:text-c-foreground shrink-0 gap-1 px-1 py-0.5 font-normal hover:bg-gray-100 dark:hover:bg-gray-800"
      onClick={handleCopy}
    >
      Copy Billing Emails
    </Button>
  );
}
